class PromotionProgressBar extends HTMLElement {
  constructor() {
    super();
    console.log('PromotionProgressBar constructor called');
    this._initializeCurrencyFormatter();
    this._initialized = false;
    this._onCartChangedListener = this._onCartChanged.bind(this);
  }

  static get observedAttributes() {
    return ["threshold", "total-price", "discount-code"];
  }

  get threshold() {
    return parseFloat(this.getAttribute("threshold") || 0);
  }

  get totalPrice() {
    return parseFloat(this.getAttribute("total-price") || 0);
  }

  connectedCallback() {
    console.log('PromotionProgressBar connected');
    if (!this._initialized) {
      this._initialized = true;
      this.progressBar = this.querySelector('progress-bar');
      this.messageElement = this.querySelector('.promotion-message');
      if (!this.progressBar) {
        console.error('No progress-bar element found in PromotionProgressBar');
      }
      if (!this.messageElement) {
        console.error('No message element found in PromotionProgressBar');
      }
      this._updateProgressBar();
      this._updateMessage();
      this._updateCheckoutButton();
      document.addEventListener("cart:change", this._onCartChangedListener);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChangedListener);
  }

  _initializeCurrencyFormatter() {
    console.log('Initializing currency formatter');
    const locale = window.themeVariables?.settings?.locale || 'en-US';
    const currencyCode = window.themeVariables?.settings?.currencyCode || 'USD';
    try {
      this._currencyFormatter = new Intl.NumberFormat(locale, { 
        style: "currency", 
        currency: currencyCode 
      });
    } catch (error) {
      console.error('Failed to initialize currency formatter:', error);
      this._currencyFormatter = {
        format: (value) => `${value.toFixed(2)}` // Fallback formatter
      };
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    console.log(`Attribute changed: ${name}, Old value: ${oldValue}, New value: ${newValue}`);
    if (oldValue === newValue) return;

    if (this._initialized) {
      this._updateProgressBar();
      this._updateMessage();
      this._updateCheckoutButton();
    }
  }

  _updateProgressBar() {
    if (this.progressBar) {
      this.progressBar.setAttribute("aria-valuemin", "0");
      this.progressBar.setAttribute("aria-valuemax", this.threshold);
      this.progressBar.setAttribute("aria-valuenow", this.totalPrice);
      this.progressBar.style.setProperty("--progress", Math.min(1, this.totalPrice / this.threshold));
    } else {
      console.error('Cannot update progress-bar: element not found');
    }
  }

  _updateMessage() {
    console.log('Updating message');
    if (!this.messageElement) {
      console.error('Cannot update message: element not found');
      return;
    }

    if (this.totalPrice >= this.threshold) {
      console.log('Threshold reached');
      this.messageElement.innerHTML = this.getAttribute("reached-message");
    } else {
      console.log('Threshold not reached');
      const remaining = Math.max(0, (this.threshold - this.totalPrice) / 100);
      const formattedRemaining = this._currencyFormatter.format(remaining);
      console.log('Remaining:', remaining, 'Formatted:', formattedRemaining);
      this.messageElement.innerHTML = this.getAttribute("unreached-message").replace(
        /\[money\]/,
        `<span class="bold">${formattedRemaining}</span>`
      );
    }
  }

  _updateCheckoutButton() {
    const checkoutButton = document.querySelector('form[action="/cart"] [name="checkout"]');
    if (checkoutButton) {
      const discountCode = this.getAttribute('discount-code');
      if (this.totalPrice >= this.threshold && discountCode) {
        checkoutButton.setAttribute('data-discount-eligible', 'true');
        
        // Update the form action to include the discount code
        const form = checkoutButton.closest('form[action="/cart"]');
        if (form) {
          const originalAction = form.getAttribute('action');
          const updatedAction = `${originalAction}?discount=${encodeURIComponent(discountCode)}`;
          form.setAttribute('action', updatedAction);
        }
      } else {
        checkoutButton.removeAttribute('data-discount-eligible');
        
        // Reset the form action to its original state
        const form = checkoutButton.closest('form[action^="/cart"]');
        if (form) {
          const currentAction = form.getAttribute('action');
          const originalAction = currentAction.split('?')[0];
          form.setAttribute('action', originalAction);
        }
      }
    }
  }

  _onCartChanged(event) {
    console.log('Cart changed event:', event);
    const priceForDiscountedItems = event.detail.cart.items.reduce((sum, item) => {
      const lineItem = document.querySelector(`line-item[data-line-key="${item.key}"]`);
      if (lineItem && lineItem.dataset.inDiscountedCollection === "true") {
        return sum + item.final_line_price;
      }
      return sum;
    }, 0);
    
    const cartDiscount = event.detail.cart.cart_level_discount_applications.reduce((sum, discountAllocation) => sum + discountAllocation.total_allocated_amount, 0);
    const newTotalPrice = priceForDiscountedItems - cartDiscount;
    console.log('New total price for discounted items:', newTotalPrice);
    
    if (newTotalPrice !== this.totalPrice) {
      this.setAttribute("total-price", newTotalPrice);
    }
  }
}

if (!window.customElements.get("promotion-progress-bar")) {
  window.customElements.define("promotion-progress-bar", PromotionProgressBar);
}